import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const LevelFiveSectionEdit = ({ data, onChange, errors, videoUrl = '', onVideoUrlChange, handleSaveLevel, handleCancelLevel, levelSaving, levelSaved, hasUnsavedChanges }) => {
  const [editingIds, setEditingIds] = useState(new Set());
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  // Normalise incoming data to always have a phrases array
  const getCurrentData = () => {
    if (Array.isArray(data)) return { phrases: data };
    if (data?.phrases && Array.isArray(data?.phrases)) return { phrases: data?.phrases };
    if (data && typeof data === 'object') return { phrases: [] };
    return { phrases: [] };
  };

  const currentData = getCurrentData();

  const addPhrase = (e) => {
    e?.preventDefault();
    e?.stopPropagation();

    const newId = Date.now();
    const newPhrase = {
      id: newId,
      header: '',
      description: ''
    };

    onChange({ phrases: [...currentData?.phrases, newPhrase] });
    // Auto-open edit mode for new phrase
    setEditingIds(prev => new Set([...prev, newId]));
  };

  const updatePhrase = (phraseId, field, value) => {
    const updatedPhrases = currentData?.phrases?.map(phrase =>
      phrase?.id === phraseId ? { ...phrase, [field]: value } : phrase
    );
    onChange({ phrases: updatedPhrases });
  };

  const removePhrase = (phraseId, e) => {
    e?.preventDefault();
    e?.stopPropagation();
    onChange({ phrases: currentData?.phrases?.filter(p => p?.id !== phraseId) });
    setEditingIds(prev => {
      const next = new Set(prev);
      next?.delete(phraseId);
      return next;
    });
  };

  const toggleEdit = (phraseId, e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setEditingIds(prev => {
      const next = new Set(prev);
      if (next?.has(phraseId)) {
        next?.delete(phraseId);
      } else {
        next?.add(phraseId);
      }
      return next;
    });
  };

  // Drag handlers
  const handleDragStart = (e, index) => {
    dragItem.current = index;
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnter = (e, index) => {
    dragOverItem.current = index;
    e?.preventDefault();
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    if (dragItem?.current === null || dragOverItem?.current === null) return;
    if (dragItem?.current === dragOverItem?.current) return;

    const phrases = [...currentData?.phrases];
    const draggedPhrase = phrases?.[dragItem?.current];
    phrases?.splice(dragItem?.current, 1);
    phrases?.splice(dragOverItem?.current, 0, draggedPhrase);

    dragItem.current = null;
    dragOverItem.current = null;
    onChange({ phrases });
  };

  const handleDragEnd = () => {
    dragItem.current = null;
    dragOverItem.current = null;
  };

  return (
    <div className="space-y-4">
      {/* Mux Video URL */}
      <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
        <h3 className="text-xl font-heading font-semibold text-white mb-4 flex items-center space-x-2">
          <Icon name="Film" size={24} className="text-blue-200" />
          <span>Level 5 Video</span>
          {hasUnsavedChanges && (
            <span className="inline-flex items-center space-x-1 bg-amber-500 bg-opacity-20 border border-amber-400 border-opacity-50 text-amber-300 text-xs px-2 py-0.5 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 inline-block" />
              <span>Unsaved</span>
            </span>
          )}
        </h3>
        <div>
          <label className="block text-sm font-medium text-blue-100 mb-2">
            Mux Playback ID
          </label>
          <input
            type="text"
            value={videoUrl}
            onChange={(e) => onVideoUrlChange?.(e?.target?.value)}
            className="w-full px-4 py-2 bg-white bg-opacity-20 border border-white border-opacity-30 rounded-xl text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
            placeholder="e.g. DS00Spx1CV902MCtPj5WknGlR017IEmVd"
          />
          <p className="text-blue-300 text-xs mt-1">
            Enter the Mux playback_id from your Mux dashboard or the videos table. Leave blank to hide the video for this level.
          </p>
        </div>
      </div>
      {/* Phrases */}
      <div className="border border-white border-opacity-20 rounded-xl p-6 bg-white bg-opacity-5">
        <div className="flex items-center justify-between mb-6">
          <h4 className="text-lg font-semibold text-white flex items-center space-x-2">
            <Icon name="Award" size={20} className="text-blue-300" />
            <span>Level 5: Mastery Analysis</span>
          </h4>

          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addPhrase}
            className="border-blue-400 text-blue-200 hover:bg-blue-500 hover:bg-opacity-20 rounded-lg"
            iconName="Plus"
            iconPosition="left"
          >
            Add Phrase
          </Button>
        </div>

        <div className="space-y-4">
          {currentData?.phrases?.length === 0 ? (
            <div className="text-center py-8 text-blue-200">
              <Icon name="MessageCircle" size={48} className="mx-auto mb-4 opacity-50" />
              <p>No phrases added yet. Click <strong>Add Phrase</strong> to begin Level 5 mastery analysis.</p>
            </div>
          ) : (
            currentData?.phrases?.map((phrase, index) => {
              const isEditing = editingIds?.has(phrase?.id);
              return (
                <div
                  key={phrase?.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragEnter={(e) => handleDragEnter(e, index)}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  onDragEnd={handleDragEnd}
                  className="border border-white border-opacity-10 rounded-lg p-4 bg-white bg-opacity-5 transition-opacity"
                >
                  {/* Card header row */}
                  <div className="flex items-center gap-2 mb-3">
                    {/* Drag handle */}
                    <div
                      className="cursor-grab active:cursor-grabbing text-blue-300 hover:text-white flex-shrink-0 px-1 select-none"
                      title="Drag to reorder"
                    >
                      <Icon name="GripVertical" size={18} />
                    </div>

                    {/* Phrase label */}
                    <span className="text-sm font-medium text-blue-300 flex-1">Phrase {index + 1}</span>

                    {/* Edit button */}
                    <button
                      type="button"
                      onClick={(e) => toggleEdit(phrase?.id, e)}
                      title={isEditing ? 'Done editing' : 'Edit phrase'}
                      className={`p-1.5 rounded-lg transition-colors ${
                        isEditing
                          ? 'bg-blue-500 bg-opacity-30 text-blue-200 hover:bg-opacity-50' :'text-blue-300 hover:bg-white hover:bg-opacity-10 hover:text-white'
                      }`}
                    >
                      <Icon name={isEditing ? 'Check' : 'Pencil'} size={15} />
                    </button>

                    {/* Delete (X) button */}
                    <button
                      type="button"
                      onClick={(e) => removePhrase(phrase?.id, e)}
                      title="Remove phrase"
                      className="p-1.5 rounded-lg text-red-300 hover:bg-red-500 hover:bg-opacity-20 hover:text-red-200 transition-colors"
                    >
                      <Icon name="X" size={15} />
                    </button>
                  </div>
                  {isEditing ? (
                    <>
                      {/* Phrase Header — editable */}
                      <div className="mb-3">
                        <label className="block text-xs font-medium text-blue-100 mb-1">
                          Phrase Header
                        </label>
                        <input
                          type="text"
                          value={phrase?.header || ''}
                          onChange={(e) => updatePhrase(phrase?.id, 'header', e?.target?.value)}
                          className="w-full px-3 py-2 bg-white bg-opacity-20 border border-white border-opacity-30 rounded-lg text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                          placeholder="Enter phrase header..."
                          autoFocus
                        />
                      </div>

                      {/* Description — editable */}
                      <div>
                        <label className="block text-xs font-medium text-blue-100 mb-1">
                          Description
                        </label>
                        <textarea
                          value={phrase?.description || ''}
                          onChange={(e) => updatePhrase(phrase?.id, 'description', e?.target?.value)}
                          className="w-full px-3 py-2 bg-white bg-opacity-20 border border-white border-opacity-30 rounded-lg text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                          placeholder="Enter phrase description..."
                          rows={4}
                          style={{ whiteSpace: 'pre-wrap' }}
                        />
                      </div>
                    </>
                  ) : (
                    /* Read-only view */
                    (<div className="pl-7">
                      {phrase?.header ? (
                        <p className="text-white font-medium text-sm mb-1">{phrase?.header}</p>
                      ) : (
                        <p className="text-blue-300 italic text-sm mb-1">No header set</p>
                      )}
                      {phrase?.description ? (
                        <p className="text-blue-100 text-sm whitespace-pre-wrap">{phrase?.description}</p>
                      ) : (
                        <p className="text-blue-300 italic text-sm">No description set</p>
                      )}
                    </div>)
                  )}
                </div>
              );
            })
          )}
        </div>

        {errors?.level5 && (
          <p className="text-red-300 text-sm mt-4">{errors?.level5}</p>
        )}
      </div>

      {/* Per-Level Save / Cancel */}
      <div className="flex justify-end items-center space-x-3 pt-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => handleCancelLevel?.('level5')}
          className="border-white border-opacity-40 text-white hover:bg-white hover:bg-opacity-10"
        >
          Cancel
        </Button>
        <Button
          type="button"
          disabled={levelSaving?.level5}
          onClick={() => handleSaveLevel?.('level5')}
          iconName={levelSaved?.level5 ? 'Check' : 'Save'}
          iconPosition="left"
          className={`bg-white text-indigo-900 hover:bg-gray-100 ${levelSaving?.level5 ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {levelSaving?.level5 ? 'Saving...' : levelSaved?.level5 ? 'Saved!' : 'Save Level 5'}
        </Button>
      </div>
    </div>
  );
};

export default LevelFiveSectionEdit;